import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X, Check, CheckCheck, Truck, Package, CheckCircle, XCircle, CreditCard, AlertCircle, ArrowRight, Leaf } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import './NotificationBell.css';

const NotificationBell = () => {
  const { isAdmin } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const [selectedNotification, setSelectedNotification] = useState(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const updatePosition = () => {
        if (!buttonRef.current) return;
        
      const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownWidth = window.innerWidth <= 480 ? Math.min(360, window.innerWidth - 32) : (window.innerWidth <= 768 ? 340 : 400);
        const viewportWidth = window.innerWidth;
        const spacing = 8; // Gap between button and dropdown
        
        // Calculate right position to align dropdown's right edge with button's right edge
        // right = distance from viewport's right edge
        let rightPosition = viewportWidth - buttonRect.right;
        
        // Ensure dropdown doesn't go off-screen on the left
        // If the dropdown would extend beyond the left edge, adjust it
        const leftEdgePosition = buttonRect.right - dropdownWidth;
        if (leftEdgePosition < 16) {
          // Not enough space on the left, align to minimum margin
          rightPosition = viewportWidth - 16;
        }
        
      setDropdownPosition({
          top: buttonRect.bottom + spacing,
          right: rightPosition
        });
      };
      
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(updatePosition, 0);
      
      // Update position on scroll and resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const normalizeLink = (link) => {
    if (!link || typeof link !== 'string') return null;
    
    // Remove leading/trailing whitespace
    let normalized = link.trim();
    
    // If it's an external URL (http/https), return as is
    if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
      return normalized;
    }
    
    // Remove duplicate /product/ patterns
    normalized = normalized.replace(/\/product\/product\//g, '/product/');
    
    // Convert /shop/product/:id to /product/:id
    normalized = normalized.replace(/^\/shop\/product\//, '/product/');
    
    // Ensure it starts with /
    if (!normalized.startsWith('/')) {
      normalized = '/' + normalized;
    }
    
    // Remove any double slashes (except after protocol)
    normalized = normalized.replace(/([^:]\/)\/+/g, '$1');
    
    // Remove trailing slash (except for root)
    if (normalized.length > 1 && normalized.endsWith('/')) {
      normalized = normalized.slice(0, -1);
    }
    
    return normalized;
  };

  const getNotificationLink = (notification) => {
    // Use explicit link if provided (normalized)
    if (notification.link) {
      return normalizeLink(notification.link);
    }

    // Generate link based on type
    const type = notification.type || '';
    const orderId = notification.order?._id || notification.order;
    const productId = notification.product?._id || notification.product;

    if (type === 'order_status') {
      if (orderId) {
        // Extract orderId if it's an object
        const id = typeof orderId === 'object' ? orderId._id || orderId : orderId;
        return `/order-confirmation/${id}`;
      }
      return '/profile';
    }

    if (type === 'payment') {
      if (orderId) {
        // Extract orderId if it's an object
        const id = typeof orderId === 'object' ? orderId._id || orderId : orderId;
        return `/order-confirmation/${id}`;
      }
      return '/profile';
    }

    if (type === 'promotion' || type === 'restock' || type === 'new_arrivals') {
      // If product is referenced, link to it
      if (productId) {
        // Extract productId if it's an object
        const id = typeof productId === 'object' ? productId._id || productId : productId;
        return `/product/${id}`;
      }
      // Otherwise link to shop
      return '/shop';
    }

    // Default to profile
    return '/profile';
  };

  const handleNotificationClick = (notification) => {
    // Show the notification detail view
    setSelectedNotification(notification);
    
    // Mark as read when viewing
    if (!notification.read) {
      markAsRead(notification._id);
    }
  };

  const handleProceedToLink = () => {
    if (!selectedNotification) return;
    
    const link = getNotificationLink(selectedNotification);
    if (link) {
      // Close everything
      setSelectedNotification(null);
      setIsOpen(false);
      
      // Handle external URLs
      if (link.startsWith('http://') || link.startsWith('https://')) {
        window.open(link, '_blank', 'noopener,noreferrer');
        return;
      }
      
      // Validate link format
      if (!link.startsWith('/')) {
        console.warn('Invalid notification link format:', link);
        navigate('/shop');
        return;
      }
      
      // Navigate to the link
      setTimeout(() => {
        try {
          navigate(link);
        } catch (error) {
          console.error('Error navigating to notification link:', error);
          navigate('/shop');
        }
      }, 100);
    }
  };

  const handleCloseDetailView = () => {
    setSelectedNotification(null);
  };

  const handleDeleteFromDetailView = () => {
    if (selectedNotification) {
      deleteNotification(selectedNotification._id);
      setSelectedNotification(null);
    }
  };


  // Get color scheme for notification type
  const getNotificationTypeColors = (notification) => {
    const type = notification.type || '';
    
    switch (type) {
      case 'promotion':
        return {
          borderColor: '#10b981',
          iconBg: '#d1fae5',
          iconColor: '#059669',
          leftBorder: '#10b981'
        };
      case 'restock':
        return {
          borderColor: '#3b82f6',
          iconBg: '#dbeafe',
          iconColor: '#2563eb',
          leftBorder: '#3b82f6'
        };
      case 'new_arrivals':
        return {
          borderColor: '#8b5cf6',
          iconBg: '#ede9fe',
          iconColor: '#7c3aed',
          leftBorder: '#8b5cf6'
        };
      case 'order_status':
        return {
          borderColor: '#f59e0b',
          iconBg: '#fef3c7',
          iconColor: '#d97706',
          leftBorder: '#f59e0b'
        };
      case 'payment':
        return {
          borderColor: '#eab308',
          iconBg: '#fef9c3',
          iconColor: '#ca8a04',
          leftBorder: '#eab308'
        };
      case 'system':
      default:
        return {
          borderColor: '#6b7280',
          iconBg: '#f3f4f6',
          iconColor: '#4b5563',
          leftBorder: '#6b7280'
        };
    }
  };

  const getNotificationIcon = (notification) => {
    const title = notification.title || '';
    const type = notification.type || '';
    
    // Check title for keywords (removing emojis)
    if (title.includes('On the Way') || title.includes('In Transit') || (type === 'order_status' && notification.message?.includes('in transit'))) {
      return <Truck size={14} className="notification-icon" />;
    }
    if (title.includes('Delivered') || (type === 'order_status' && notification.message?.includes('delivered'))) {
      return <Package size={14} className="notification-icon" />;
    }
    if (title.includes('Payment Approved') || title.includes('Payment Received')) {
      return <CheckCircle size={14} className="notification-icon" />;
    }
    if (title.includes('Cancelled')) {
      return <XCircle size={14} className="notification-icon" />;
    }
    if (title.includes('Payment Failed')) {
      return <AlertCircle size={14} className="notification-icon" />;
    }
    if (type === 'payment') {
      return <CreditCard size={14} className="notification-icon" />;
    }
    if (type === 'promotion' || type === 'new_arrivals') {
      return <Leaf size={14} className="notification-icon" />;
    }
    if (type === 'restock') {
      return <Package size={14} className="notification-icon" />;
    }
    
    // Default icon
    return <Bell size={14} className="notification-icon" />;
  };

  const cleanNotificationTitle = (title) => {
    // Remove emoji characters
    return title.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '').trim();
  };

  const getNotificationTypeLabel = (type) => {
    switch (type) {
      case 'admin_new_order':
        return 'New Order';
      case 'admin_low_stock':
        return 'Low Stock';
      case 'admin_out_of_stock':
        return 'Out of Stock';
      case 'new_arrivals':
        return 'New Arrivals';
      case 'order_status':
        return 'Order';
      case 'payment':
        return 'Payment';
      case 'promotion':
        return 'Promotion';
      case 'restock':
        return 'Restock';
      case 'system':
      default:
        return 'System';
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadNotifications = notifications.filter(n => !n.read);
  const readNotifications = notifications.filter(n => n.read).slice(0, 5);

  return (
    <>
      <div className="notification-bell-container">
        <button
          ref={buttonRef}
          className="notification-bell-button"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Notifications"
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </button>
      </div>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="notification-dropdown"
          style={{
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
            left: 'auto'
          }}
        >
          {/* Notification Detail View */}
          {selectedNotification ? (
            <div className="notification-detail-view">
              <div className="notification-detail-header">
                <button 
                  className="notification-back-btn"
                  onClick={handleCloseDetailView}
                >
                  <ArrowRight size={16} style={{ transform: 'rotate(180deg)' }} />
                  Back
                </button>
                <span 
                  className="notification-type-badge"
                  style={{
                    background: getNotificationTypeColors(selectedNotification).iconBg,
                    color: getNotificationTypeColors(selectedNotification).iconColor
                  }}
                >
                  {getNotificationTypeLabel(selectedNotification.type)}
                </span>
              </div>
              
              <div className="notification-detail-content">
                <div 
                  className="notification-detail-icon"
                  style={{
                    background: getNotificationTypeColors(selectedNotification).iconBg,
                    color: getNotificationTypeColors(selectedNotification).iconColor
                  }}
                >
                  {getNotificationIcon(selectedNotification)}
                </div>
                
                <h3 className="notification-detail-title">
                  {cleanNotificationTitle(selectedNotification.title)}
                </h3>
                
                <p className="notification-detail-message">
                  {selectedNotification.message}
                </p>
                
                {selectedNotification.order && (
                  <div className="notification-detail-order">
                    {selectedNotification.order.orderNumber && (
                      <div className="detail-order-item">
                        <span className="detail-order-label">Order Number:</span>
                        <span className="detail-order-value">{selectedNotification.order.orderNumber}</span>
                      </div>
                    )}
                    {selectedNotification.order.status && (
                      <div className="detail-order-item">
                        <span className="detail-order-label">Status:</span>
                        <span className="detail-order-value">{selectedNotification.order.status}</span>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="notification-detail-time">
                  {formatDateTime(selectedNotification.createdAt)}
                </div>
              </div>
              
              <div className="notification-detail-actions">
                {getNotificationLink(selectedNotification) && (
                  <button 
                    className="notification-proceed-btn"
                    onClick={handleProceedToLink}
                  >
                    View Details
                  </button>
                )}
                <button 
                  className="notification-disregard-btn"
                  onClick={handleCloseDetailView}
                >
                  <X size={16} />
                  Close
                </button>
                <button 
                  className="notification-delete-btn"
                  onClick={handleDeleteFromDetailView}
                >
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <>
          <div className="notification-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button
                className="mark-all-read-btn"
                onClick={() => {
                  markAllAsRead();
                }}
                title="Mark all as read"
              >
                <CheckCheck size={14} />
                Mark all read
              </button>
            )}
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="notification-empty">
                <Bell size={32} />
                <p>No notifications</p>
              </div>
            ) : (
              <>
                {unreadNotifications.length > 0 && (
                  <>
                    <div className="notification-section-title">New</div>
                    {unreadNotifications.map(notification => (
                      <div
                        key={notification._id}
                        className={`notification-item unread`}
                        onClick={() => handleNotificationClick(notification)}
                        style={{
                          borderLeft: `3px solid ${getNotificationTypeColors(notification).leftBorder}`
                        }}
                      >
                        <div 
                          className="notification-icon-wrapper"
                          style={{
                            background: getNotificationTypeColors(notification).iconBg
                          }}
                        >
                          <div style={{ color: getNotificationTypeColors(notification).iconColor }}>
                            {getNotificationIcon(notification)}
                          </div>
                        </div>
                        <div className="notification-content">
                          <div className="notification-header-row">
                            <div className="notification-title-section">
                              <div className="notification-title">{cleanNotificationTitle(notification.title)}</div>
                            </div>
                            <div className="notification-badges">
                              <span 
                                className="notification-type-badge"
                                style={{
                                  background: getNotificationTypeColors(notification).iconBg,
                                  color: getNotificationTypeColors(notification).iconColor
                                }}
                              >
                                {getNotificationTypeLabel(notification.type)}
                              </span>
                              <span className="notification-status-badge unread-badge">Unread</span>
                            </div>
                          </div>
                          <div className="notification-footer-row">
                            <span className="notification-time">{formatDateTime(notification.createdAt)}</span>
                          </div>
                        </div>
                        <div className="notification-actions">
                          <button
                            className="notification-action-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notification._id);
                            }}
                            title="Mark as read"
                          >
                            <Check size={8} />
                          </button>
                          <button
                            className="notification-action-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification._id);
                            }}
                            title="Delete"
                          >
                            <X size={8} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {readNotifications.length > 0 && (
                  <>
                    {unreadNotifications.length > 0 && (
                      <div className="notification-section-title">Earlier</div>
                    )}
                    {readNotifications.map(notification => (
                      <div
                        key={notification._id}
                        className="notification-item read"
                        onClick={() => handleNotificationClick(notification)}
                        style={{
                          borderLeft: `3px solid ${getNotificationTypeColors(notification).leftBorder}`
                        }}
                      >
                        <div 
                          className="notification-icon-wrapper"
                          style={{
                            background: getNotificationTypeColors(notification).iconBg
                          }}
                        >
                          <div style={{ color: getNotificationTypeColors(notification).iconColor }}>
                            {getNotificationIcon(notification)}
                          </div>
                        </div>
                        <div className="notification-content">
                          <div className="notification-header-row">
                            <div className="notification-title-section">
                              <div className="notification-title">{cleanNotificationTitle(notification.title)}</div>
                            </div>
                            <div className="notification-badges">
                              <span 
                                className="notification-type-badge"
                                style={{
                                  background: getNotificationTypeColors(notification).iconBg,
                                  color: getNotificationTypeColors(notification).iconColor
                                }}
                              >
                                {getNotificationTypeLabel(notification.type)}
                              </span>
                            </div>
                          </div>
                          <div className="notification-footer-row">
                            <span className="notification-time">{formatDateTime(notification.createdAt)}</span>
                          </div>
                        </div>
                        <button
                          className="notification-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(notification._id);
                          }}
                          title="Delete"
                        >
                          <X size={8} />
                        </button>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
          
          <div className="notification-footer">
            <Link
              to={isAdmin() ? "/admin/notifications" : "/notifications"}
              className="view-all-link"
              onClick={() => setIsOpen(false)}
            >
              <span>View All Notifications</span>
            </Link>
          </div>
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default NotificationBell;



